import { createElement, useMemo, type ReactElement } from 'react';
import {
  getPlatformIcon,
  getIdeClientDisplayLabel,
} from '@/components/Inventory/UserSecurityPostureTab/platform-utils';
import {
  buildLoggedInTooltip,
  groupClientsByCoverage,
  isLoggedInUserMismatch,
  loggedInMatchChipClass,
} from '@/components/Inventory/UserSecurityPostureTab/client-utils';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import type {
  ClientActivity,
  UserSecurityPostureDataTableItem,
} from '@kirin/api-client/contracts/user-security-posture';

interface UserExpandedContentProps {
  row: UserSecurityPostureDataTableItem;
}

function formatKirinLastActive(lastActiveAt: string | null): string {
  return lastActiveAt
    ? new Date(lastActiveAt)
        .toISOString()
        .replace('T', ' ')
        .replace('Z', ' UTC')
    : '—';
}

/**
 * Header card for one platform/client (KIR-3657): icon + label, then the
 * logged-in account signed in to that tool — coloured by match (personal = red,
 * corporate = amber, matched/plain otherwise) with the mismatch tooltip — and
 * the "Kirin Last Active" timestamp on the right. Merging the account onto the
 * platform row gives one consistent list instead of a separate accounts section.
 */
function PlatformHeaderCard({
  client,
}: {
  client: ClientActivity;
}): ReactElement {
  const {
    client: platform,
    displayName,
    lastActiveAt,
    loggedInUserEmail,
    loggedInUserMatch,
    coverageStatus,
  } = client;

  const mismatch = isLoggedInUserMismatch(loggedInUserEmail, loggedInUserMatch);
  const emailChip = loggedInUserEmail ? (
    <span
      className={
        mismatch
          ? `rounded px-1.5 py-0.5 text-xs font-medium ${loggedInMatchChipClass(loggedInUserMatch)}`
          : 'text-xs text-muted-foreground'
      }
    >
      {loggedInUserEmail}
    </span>
  ) : null;
  // Flagged (mismatch) chips explain why on hover, matching the posture badge.
  const tooltipText = mismatch
    ? buildLoggedInTooltip(
        coverageStatus,
        lastActiveAt,
        loggedInUserMatch,
        loggedInUserEmail
      )
    : undefined;

  return (
    <div className="flex items-center justify-between gap-2 border rounded-md px-3 py-2 bg-background">
      <div className="flex items-center gap-2 min-w-0">
        {createElement(getPlatformIcon(platform), {
          className: 'h-4 w-4 shrink-0',
        })}
        <span className="font-medium">
          {getIdeClientDisplayLabel(platform, displayName)}
        </span>
        {emailChip && (
          <>
            <span className="text-muted-foreground">·</span>
            {tooltipText ? (
              <Tooltip>
                <TooltipTrigger asChild>{emailChip}</TooltipTrigger>
                <TooltipContent>{tooltipText}</TooltipContent>
              </Tooltip>
            ) : (
              emailChip
            )}
          </>
        )}
      </div>
      <div className="text-xs text-muted-foreground font-mono shrink-0">
        Kirin Last Active: {formatKirinLastActive(lastActiveAt)}
      </div>
    </div>
  );
}

/**
 * The MCP-servers block shown under a tool that runs MCP servers (KIR-2916).
 */
function McpServersBlock({ mcps }: { mcps: string[] }): ReactElement {
  return (
    <div className="px-1">
      <div className="text-xs font-medium text-muted-foreground mb-1.5 uppercase tracking-wider">
        MCP Servers
      </div>
      {mcps.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {mcps.map(mcp => (
            <div
              key={mcp}
              className="inline-flex items-center rounded-md border px-2.5 py-0.5 text-xs font-medium transition-colors bg-background text-foreground"
            >
              {mcp}
            </div>
          ))}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground">—</div>
      )}
    </div>
  );
}

/** Accounts for MCP configs counted in the row's MCP column but not listed as tools (KIR-4109). */
function OrphanedMcpHint(): ReactElement {
  return (
    <div className="text-sm text-muted-foreground">
      MCP server configs were found with no matching tool install — see the MCP
      Servers tab.
    </div>
  );
}

/**
 * Expanded row content for the Users DataTable.
 *
 * Lists every IDE/platform we actually **detected** on the user's machine —
 * covered, supported, and unknown-coverage clients, in that order — matching
 * the client-details modal (KIR-3541). Each platform row shows the account
 * signed in to that tool — coloured by match with the mismatch tooltip
 * (KIR-2916) — plus its last Kirin-active time, and, for tools that run MCP
 * servers, the per-tool MCP breakdown nested underneath.
 *
 * An MCP config is **not** an install signal (KIR-4109): `mcpsByPlatform` comes
 * from discovered config files, so a leftover config for an uninstalled tool
 * must never put that tool in this list. Those configs surface in the MCP
 * Servers tab instead.
 */
export function UserExpandedContent({
  row,
}: UserExpandedContentProps): ReactElement {
  // MCP servers keyed by platform, only platforms that actually run servers.
  const mcpByPlatform = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const p of row.mcpsByPlatform) {
      if (p.mcpCount > 0) map.set(p.platform, p.mcps ?? []);
    }
    return map;
  }, [row.mcpsByPlatform]);

  // Detected clients only: covered first, then supported, then
  // unknown-coverage — matching the client-details modal so the expanded row is
  // not a subset (KIR-3541). Platforms known *only* from an MCP config are
  // deliberately excluded (KIR-4109) — a stale config is not an install.
  const tools = useMemo<ClientActivity[]>(() => {
    const grouped = groupClientsByCoverage(row.clients ?? []);
    return [...grouped.covered, ...grouped.supported, ...grouped.unknown];
  }, [row.clients]);

  // Configs dropped above still count towards the collapsed row's MCP column,
  // so the hint has to fire even for a user who does have real installs.
  const hasOrphanedMcps = useMemo(() => {
    const detected = new Set((row.clients ?? []).map(c => c.client));
    return row.mcpsByPlatform.some(
      p => p.mcpCount > 0 && !detected.has(p.platform)
    );
  }, [row.mcpsByPlatform, row.clients]);

  if (tools.length === 0) {
    return (
      <div className="flex flex-col gap-2 pl-4 py-4">
        <div className="text-sm text-muted-foreground">
          No IDEs or platforms for this user.
        </div>
        {/* mcpCount, not hasOrphanedMcps: with no clients every config is
            orphaned anyway, and mcpCount holds up if mcpsByPlatform is empty
            or absent (the two can drift — KIR-4240). */}
        {row.mcpCount > 0 && <OrphanedMcpHint />}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 pl-4 py-4">
      {tools.map(client => {
        const mcps = mcpByPlatform.get(client.client);
        return (
          <div key={client.client} className="flex flex-col gap-2">
            <PlatformHeaderCard client={client} />
            {mcps && <McpServersBlock mcps={mcps} />}
          </div>
        );
      })}
      {hasOrphanedMcps && <OrphanedMcpHint />}
    </div>
  );
}
